import { cloneDeep, get, isArray, isObjectLike, set } from "lodash";
import {
  IExpressionFunction,
  IFieldArray,
  IFieldConfigurationObject,
  IFieldGroup,
  IFormOptionsAPI,
  ITemplateOptions,
  ITemplateScope,
  IWatcher,
} from "AngularFormly";
import {
  RecordsUtilsService,
  vsName as RecordsUtilsServiceName,
} from "../../../../utils";

export interface IRecordRepeaterTemplateOptions extends ITemplateOptions {
  displayNameSingular: string;
  displayNamePlural?: string;
  allowAdd: boolean;
  allowAddBottom: boolean;
  showHeader: boolean;
  repeaterFields: IFieldArray;
  staticFields?: IFieldArray[];
  afterAdd?: (model: any, parentModel: any) => void;
  hideExpression?: string | IExpressionFunction;
  watcher?: IWatcher | IWatcher[];
}

export interface RowState {
  fields: IFieldArray;
  formOptions: IFormOptionsAPI;
  editing: boolean;
}

export interface IRecordRepeaterScope extends ITemplateScope {
  rowState: RowState[];
  getFieldValue: () => unknown;
  addRow: (index?: number) => void;
  deleteRow: (index: number) => void;
  getTrackByValue: (row: unknown) => any;
  staticFields: IFieldArray[];
}

export class RecordRepeaterController {
  static $inject = ["$scope", RecordsUtilsServiceName];

  protected readonly rowState: RowState[];

  protected get fieldValue(): unknown[] | undefined {
    return <unknown[]>get(this.$scope.model, this.$scope.options.key);
  }
  protected set fieldValue(value: unknown[] | undefined) {
    set(this.$scope.model ?? {}, this.$scope.options.key, value);
  }

  protected get templateOptions(): IRecordRepeaterTemplateOptions {
    return <IRecordRepeaterTemplateOptions>this.$scope.to;
  }

  constructor(
    protected readonly $scope: IRecordRepeaterScope,
    protected readonly recordUtils: RecordsUtilsService
  ) {
    const rowState = this.getRowState(this.fieldValue?.length ?? 0);
    this.rowState = $scope.rowState = rowState;
    $scope.getFieldValue = () => this.fieldValue;
    $scope.addRow = (index?: number) => this.addRow(index);
    $scope.deleteRow = (index: number) => this.deleteRow(index);
    $scope.getTrackByValue = (row) => this.getTrackByValue(row);
    $scope.staticFields = this.templateOptions.staticFields ?? [];

    this.assignIdsToModel(this.fieldValue ?? []);
  }

  addRow(model?: any) {
    const newRowValue = model ? cloneDeep(model) : {};
    this.recordUtils.stripIds(newRowValue);
    const fieldValue = this.fieldValue ?? (this.fieldValue = []);

    const newFieldState = this.buildRowState(
      fieldValue.length,
      this.cloneFields()
    );

    this.rowState.push(newFieldState);
    const modelIndex = fieldValue.indexOf(model);

    fieldValue.splice(
      modelIndex < 0 ? fieldValue.length : modelIndex + 1,
      0,
      newRowValue
    );
    this.templateOptions.afterAdd?.call(this, newRowValue, this.$scope.model);

    return newRowValue;
  }

  private assignIdsToModel(values: any[]): any {
    values.forEach((v) => this.recordUtils.assignId(v));
  }

  getTrackByValue(row: any): number {
    return this.recordUtils.getRecordId(row)!;
  }

  deleteRow(model?: unknown) {
    if (!this.fieldValue) {
      return;
    }
    const index = this.fieldValue.indexOf(model);
    this.fieldValue.splice(index, 1);
    this.rowState.splice(this.rowState.length - 1, 1);
  }

  moveUp(model: unknown) {
    if (!this.fieldValue) {
      return;
    }
    const index = this.fieldValue.indexOf(model);
    if (index < 1) {
      return;
    }
    this.fieldValue.splice(index, 1);
    this.fieldValue.splice(index - 1, 0, model);
  }

  moveDown(model: unknown) {
    if (!this.fieldValue) {
      return;
    }
    const index = this.fieldValue.indexOf(model);
    if (index < 0 || index === this.fieldValue.length - 1) {
      return;
    }
    this.fieldValue.splice(index, 1);
    this.fieldValue.splice(index + 1, 0, model);
  }

  getRowIndex(model: unknown) {
    return this.fieldValue?.indexOf(model) ?? -1;
  }

  private getRowState(count: number): RowState[] {
    const formattedFields = Array.from({ length: count }).map((_, index) =>
      this.buildRowState(index, this.cloneFields())
    );
    return formattedFields;
  }

  protected buildRowState(index: number, fields: IFieldArray): RowState {
    const formState = { getRepeaterController: () => this };
    this.assignNewFieldIds(fields, index);
    return { fields, formOptions: { formState }, editing: false };
  }

  protected cloneFields() {
    return cloneDeep(this.templateOptions.repeaterFields);
  }

  private assignNewFieldIds(fields: IFieldArray, index: number) {
    fields.forEach((field) => {
      const fieldGroup = (field as IFieldGroup)?.fieldGroup;
      if (fieldGroup) {
        this.assignNewFieldIds(fieldGroup, index);
        return; // fieldGroups don't need an ID
      }

      if (field.templateOptions && field.templateOptions.fields) {
        this.assignNewFieldIds(field.templateOptions.fields, index);
      }
      const fieldConfigurationObject = field as IFieldConfigurationObject;
      const name =
        fieldConfigurationObject.name || fieldConfigurationObject.key;
      fieldConfigurationObject.id =
        fieldConfigurationObject.name = `${this.$scope.id}[${index}].${name}`;
    });
  }
}
